
import { defineComponent, ref, onMounted } from "vue"
import { Actions } from "@/store/enums/StoreEnums"
import { useStore } from "vuex"
import { useRoute, useRouter } from "vue-router"
import { setCurrentPageBreadcrumbs } from "@/core/helpers/breadcrumb"
import { ElMessage } from "element-plus"
import type { ElForm } from "element-plus"
import { CardSeriesOption } from "@/store/modules/CardSeriesModule"
import { CardRarityOption } from "@/store/modules/CardRarityModule"
import ItemsManager from "./ItemsManager.vue"
import ImageCropUploader from "@/components/common/ImageCropUploader.vue"

type FormInstance = InstanceType<typeof ElForm>;

interface CardPoolForm {
  card_series_id: number | null;
  name: string;
  image: string | null;
  list_image: string | null;
  description: string | null;
  price: number;
  energy_limit: number;
  cards_per_pack: number;
  guaranteed_rarities: string[];
  energy_trigger_rarities: string[];
  sort: number;
  enabled: boolean;
  is_increase_membership_point: boolean;
  is_presale: boolean;
}

export default defineComponent({
  components: {
    ItemsManager,
    ImageCropUploader
  },
  setup() {
    const store = useStore()
    const route = useRoute()
    const router = useRouter()
    const formRef = ref<FormInstance>()

    const isNew = !route.params.id
    const savedPoolId = ref<number | null>(isNew ? null : Number(route.params.id))

    const cardSeriesOptions = ref<CardSeriesOption[]>([])
    const cardRarityOptions = ref<CardRarityOption[]>([])

    onMounted(() => {
      if (isNew) {
        setCurrentPageBreadcrumbs("新建卡池", ["抽卡系统", "卡池管理;card_pools"])
      } else {
        setCurrentPageBreadcrumbs("编辑卡池", ["抽卡系统", "卡池管理;card_pools"])
      }
      loadOptions()
    })

    const cardPool = ref<CardPoolForm>({
      card_series_id: null,
      name: '',
      image: null,
      list_image: null,
      description: null,
      price: 0,
      energy_limit: 10,
      cards_per_pack: 1,
      guaranteed_rarities: [],
      energy_trigger_rarities: [],
      sort: 0,
      enabled: true,
      is_increase_membership_point: false,
      is_presale: false
    })

    const rules = ref({
      card_series_id: [
        { required: true, message: '请选择所属系列', trigger: 'change' }
      ],
      name: [
        { required: true, message: '请输入卡池名称', trigger: 'blur' },
        { min: 1, max: 100, message: '名称长度需在 1-100 之间', trigger: 'blur' }
      ],
      price: [
        { required: true, message: '请输入抽卡价格', trigger: 'blur' }
      ]
    })

    const loadOptions = () => {
      store.dispatch(Actions.GET_ALL_CARD_SERIES).then(() => {
        cardSeriesOptions.value = store.getters.currentAllCardSeries
      })
      store.dispatch(Actions.GET_ALL_CARD_RARITIES).then(() => {
        cardRarityOptions.value = store.getters.currentAllCardRarities
      })
    }

    // 加载详情（编辑模式）
    if (!isNew) {
      store
        .dispatch(Actions.GET_CARD_POOL, route.params.id)
        .then(() => {
          const data = store.getters.currentCardPool
          cardPool.value = {
            card_series_id: data.card_series_id,
            name: data.name,
            image: data.image,
            list_image: data.list_image,
            description: data.description,
            price: data.price,
            energy_limit: data.energy_limit,
            cards_per_pack: data.cards_per_pack || 1,
            guaranteed_rarities: data.guaranteed_rarities || [],
            energy_trigger_rarities: data.energy_trigger_rarities || [],
            sort: data.sort || 0,
            enabled: data.enabled,
            is_increase_membership_point: data.is_increase_membership_point || false,
            is_presale: data.is_presale || false
          }
        })
        .catch(() => {
          ElMessage.error('获取卡池详情失败')
        })
    }

    const onSubmit = (formEl: FormInstance | undefined) => {
      if (!formEl) return

      formEl.validate((valid) => {
        if (valid) {
          const params = { ...cardPool.value }

          if (isNew) {
            store
              .dispatch(Actions.CREATE_CARD_POOL, params)
              .then((data: { success: boolean; error_msg?: string; card_pool?: { id: number } }) => {
                if (data.success) {
                  ElMessage.success('创建成功')
                  // 创建成功后跳转到编辑页面，以便配置卡牌
                  if (data.card_pool?.id) {
                    router.replace({ name: 'card_pools_edit', params: { id: data.card_pool.id } })
                    savedPoolId.value = data.card_pool.id
                  } else {
                    router.push({ name: 'card_pools' })
                  }
                } else {
                  ElMessage.error(data.error_msg || '创建失败')
                }
              })
              .catch(() => {
                ElMessage.error('创建失败')
              })
          } else {
            store
              .dispatch(Actions.UPDATE_CARD_POOL, {
                id: route.params.id,
                values: params
              })
              .then((data: { success: boolean; error_msg?: string }) => {
                if (data.success) {
                  ElMessage.success('更新成功')
                } else {
                  ElMessage.error(data.error_msg || '更新失败')
                }
              })
              .catch(() => {
                ElMessage.error('更新失败')
              })
          }
        }
      })
    }

    const onCancel = () => {
      router.push({ name: 'card_pools' })
    }

    const onItemsUpdate = () => {
      // 卡牌配置更新后可以刷新卡池详情
      if (savedPoolId.value) {
        store.dispatch(Actions.GET_CARD_POOL, savedPoolId.value)
      }
    }

    return {
      formRef,
      cardPool,
      rules,
      isNew,
      savedPoolId,
      cardSeriesOptions,
      cardRarityOptions,
      onSubmit,
      onCancel,
      onItemsUpdate
    }
  }
})
